import React from 'react';
import { Box, Typography, Button, Stack, Container, Chip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  badge?: string;
  primaryCta?: {
    label: string;
    path: string;
  };
  secondaryCta?: {
    label: string;
    path: string;
  };
  trustBullets?: string[];
  heroImage?: {
    src: string;
    alt: string;
  };
  variant?: 'default' | 'centered';
}

const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  badge,
  primaryCta,
  secondaryCta,
  trustBullets,
  heroImage,
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const centered = variant === 'centered' || Boolean(heroImage);

  return (
    <Box
      sx={{
        pt: { xs: 8, md: 12 },
        pb: heroImage ? 0 : { xs: 6, md: 10 },
        background: 'linear-gradient(180deg, #f7f7f8 0%, #ffffff 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.4,
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, #000 50%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, #000 50%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: centered ? 'center' : { xs: 'center', md: 'left' },
            position: 'relative',
            zIndex: 1,
            maxWidth: centered ? 860 : '100%',
            mx: centered ? 'auto' : 0,
          }}
        >
          {badge && (
            <Chip
              label={badge}
              size="small"
              sx={{
                mb: 3,
                fontWeight: 600,
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
              }}
            />
          )}
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' },
              lineHeight: 1.08,
              letterSpacing: '-0.03em',
              mb: 3,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              color: 'text.secondary',
              mb: 4,
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              lineHeight: 1.6,
              maxWidth: centered ? 640 : { xs: '100%', md: '90%' },
              mx: centered ? 'auto' : 0,
            }}
          >
            {subtitle}
          </Typography>
          {(primaryCta || secondaryCta) && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                justifyContent: centered ? 'center' : { xs: 'center', md: 'flex-start' },
                alignItems: 'center',
              }}
            >
              {primaryCta && (
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(primaryCta.path)}
                  sx={{
                    minWidth: { xs: '100%', sm: '220px' },
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  {primaryCta.label}
                </Button>
              )}
              {secondaryCta && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(secondaryCta.path)}
                  sx={{
                    minWidth: { xs: '100%', sm: '200px' },
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                  }}
                >
                  {secondaryCta.label}
                </Button>
              )}
            </Stack>
          )}
          {trustBullets && trustBullets.length > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 3 }}
              sx={{
                mt: 3,
                justifyContent: centered ? 'center' : { xs: 'center', md: 'flex-start' },
                alignItems: 'center',
              }}
            >
              {trustBullets.map((bullet) => (
                <Stack key={bullet} direction="row" spacing={0.75} alignItems="center">
                  <CheckCircleOutlineIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {bullet}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
        {heroImage && (
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              mt: { xs: 5, md: 7 },
              mx: 'auto',
              maxWidth: 1000,
              borderRadius: '16px 16px 0 0',
              overflow: 'hidden',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderBottom: 'none',
              boxShadow: '0 -10px 60px rgba(0, 0, 0, 0.12)',
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                px: 2,
                py: 1.25,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                backgroundColor: '#f7f7f8',
              }}
            >
              {['#ff5f57', '#febc2e', '#28c840'].map((color) => (
                <Box
                  key={color}
                  sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }}
                />
              ))}
            </Box>
            <Box
              component="img"
              src={heroImage.src}
              alt={heroImage.alt}
              sx={{ display: 'block', width: '100%', height: 'auto' }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default HeroSection;
